import { useState } from 'react';
import type { MfaEnrolment, MfaStatus } from '@dru/types';
import { AlertTriangle, Check, KeyRound, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  Alert, Button, Card, CardBody, CardHeader, ConfirmDialog, ErrorState, Field, Input, StatusBadge, TextSkeleton,
  useToast,
} from '@dru/ui';
import { api } from '../lib/api';
import { useQuery } from '../lib/useQuery';
import { errorMessage } from '../lib/auth';
import { PageHeader } from '../layouts/ConsoleShell';

/**
 * The account's own security.
 *
 * Reachable from every console rather than living under `/admin`, because the
 * second factor is not a platform feature — a certifying physician signing a
 * fitness decision has as much reason to hold one as a platform
 * administrator, and burying it in someone else's navigation is how a control
 * ends up unused.
 *
 * The screen is built around the one thing people get wrong here: recovery
 * codes are shown once. So they are not a footnote after success — they take
 * over the card, and the card refuses to be dismissed until it has been
 * acknowledged.
 */
export function Security() {
  const status = useQuery<MfaStatus>(() => api.get('/auth/mfa'), []);

  const [enrolment, setEnrolment] = useState<MfaEnrolment | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingOff, setConfirmingOff] = useState(false);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const begin = () => run(async () => {
    setEnrolment(await api.post<MfaEnrolment>('/auth/mfa/enrol'));
    setCode('');
  });

  const activate = () => run(async () => {
    const res = await api.post<{ recoveryCodes: string[] }>('/auth/mfa/activate', { code });
    setCodes(res.recoveryCodes);
    setEnrolment(null);
    setCode('');
    status.reload();
  });

  // Throws, so the confirmation stays open saying why (usually a stale code).
  const disable = async () => {
    await api.post('/auth/mfa/disable', { code });
    setCode('');
    setConfirmingOff(false);
    status.reload();
  };

  const regenerate = () => run(async () => {
    const res = await api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', { code });
    setCodes(res.recoveryCodes);
    setCode('');
    status.reload();
  });

  if (status.error) return <ErrorState detail={status.error} onRetry={status.reload} />;

  const enabled = status.data?.enabled ?? false;
  const remaining = status.data?.recoveryCodesRemaining ?? 0;

  return (
    <div className="max-w-2xl space-y-5">
      <PageHeader
        title="Security"
        hint="Two-factor authentication protects the actions that a password alone should not be enough for — looking inside a company, granting authority, or signing a fitness decision."
      />

      {/* Shown once, and only once. It takes the whole card because a person
          who scrolls past this has lost their way back into the account. */}
      {codes && <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />}

      {confirmingOff && (
        <ConfirmDialog
          title="Turn off two-factor authentication?"
          what="From now on your password alone signs you in to this account."
          affects="Acts that need a second factor — looking inside a company, granting authority, signing a fitness decision — are refused until you set it up again. Your recovery codes stop working."
          confirmLabel="Turn off two-factor"
          tone="danger"
          onCancel={() => setConfirmingOff(false)}
          onConfirm={disable}
        />
      )}

      {!codes && (
        <Card>
          <CardHeader
            title="Two-factor authentication"
            hint="A six-digit code from an authenticator app on your phone."
            actions={
              status.loading ? null : (
                <StatusBadge tone={enabled ? 'success' : 'warning'} dot>
                  {enabled ? 'Active' : 'Not set up'}
                </StatusBadge>
              )
            }
          />
          <CardBody className="space-y-4">
            {status.loading && <TextSkeleton lines={3} />}

            {error && <Alert tone="danger">{error}</Alert>}

            {/* ── Not set up ── */}
            {!status.loading && !enabled && !enrolment && (
              <>
                <p className="text-small text-neutral-600">
                  You will need an authenticator app — Google Authenticator, Authy, 1Password
                  or the one built into your password manager.
                </p>
                <Button onClick={begin} loading={busy} icon={<ShieldCheck className="h-4 w-4" aria-hidden />}>
                  Set up two-factor authentication
                </Button>
              </>
            )}

            {/* ── Enrolling ── */}
            {enrolment && (
              <div className="space-y-4">
                <ol className="space-y-4">
                  <li>
                    <div className="text-body font-semibold text-neutral-800">
                      1 · Scan this with your authenticator app
                    </div>
                    <img
                      src={enrolment.qrDataUrl}
                      alt="Two-factor authentication setup code"
                      className="mt-2 rounded border border-neutral-200 bg-white p-2"
                      width={200}
                      height={200}
                    />
                    <details className="mt-2">
                      <summary className="cursor-pointer py-1 text-caption text-neutral-600">
                        Cannot scan? Type this instead
                      </summary>
                      <code className="mt-1.5 block break-all rounded bg-neutral-100 px-2.5 py-2 font-mono text-small text-neutral-800">
                        {enrolment.secret.replace(/(.{4})/g, '$1 ').trim()}
                      </code>
                    </details>
                  </li>
                  <li>
                    <Field label="2 · Enter the six-digit code it shows">
                      {(p) => (
                        <Input
                          {...p}
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          placeholder="123456"
                          className="w-40 font-mono tracking-widest"
                        />
                      )}
                    </Field>
                  </li>
                </ol>

                <div className="flex items-center gap-2">
                  <Button onClick={activate} loading={busy} disabled={code.length < 6}>
                    Turn it on
                  </Button>
                  <Button variant="secondary" onClick={() => { setEnrolment(null); setError(null); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* ── Active ── */}
            {!status.loading && enabled && (
              <div className="space-y-4">
                <p className="text-small text-neutral-600">
                  You will be asked for a code each time you sign in.
                </p>

                {/* The number that decides whether a lost phone is an
                    inconvenience or a support ticket. */}
                <div className="flex items-center gap-2 text-small">
                  <KeyRound className="h-4 w-4 text-neutral-400" aria-hidden />
                  <span className="text-neutral-700">
                    {remaining} of {status.data?.recoveryCodesTotal ?? 0} recovery codes unused
                  </span>
                </div>
                {remaining <= 2 && (
                  <Alert tone="warning">
                    <AlertTriangle className="mr-1.5 inline h-4 w-4" aria-hidden />
                    You are nearly out of recovery codes. Generate a new set now, while you
                    still have a working device to authorise it with.
                  </Alert>
                )}

                <Field
                  label="Current code from your authenticator"
                  hint="Needed for either change below."
                  info="So a borrowed session cannot make them."
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      placeholder="123456"
                      className="w-40 font-mono tracking-widest"
                    />
                  )}
                </Field>

                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="secondary" onClick={regenerate}
                          loading={busy} disabled={code.length < 6}
                          icon={<KeyRound className="h-4 w-4" aria-hidden />}>
                    New recovery codes
                  </Button>
                  <span className="text-caption text-neutral-500">Your current codes stop working.</span>
                </div>

                {/* Turning the second factor off is set apart and confirmed:
                    it is the one change here that weakens the account. */}
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-input border border-danger-600/30 px-3 py-3">
                  <p className="min-w-0 flex-[1_1_16rem] text-small text-neutral-700">
                    <span className="font-medium text-neutral-900">Turn off two-factor.</span>{' '}
                    Your password alone will sign you in, and acts that need a second factor are refused until you set it up again.
                  </p>
                  <Button variant="danger" onClick={() => setConfirmingOff(true)} disabled={code.length < 6}
                          icon={<ShieldOff className="h-4 w-4" aria-hidden />}>
                    Turn off
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

/**
 * The codes, once.
 *
 * No dismiss in the corner and no navigation away by accident: the only way
 * out is the button that says the codes have been saved. A modal somebody can
 * click past is how people end up locked out of an account that was working
 * ten seconds earlier.
 */
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const { toast } = useToast();

  async function copy() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast('success', 'Recovery codes copied. Paste them somewhere you can reach without your phone.');
    } catch {
      toast('danger', 'Could not copy. Select the codes and copy them, or print them.');
    }
  }

  return (
    <Card>
      <CardHeader
        title="Save your recovery codes"
        hint="Each one signs you in a single time if you lose your phone."
      />
      <CardBody className="space-y-4">
        {/* A warning, not background: it stays on screen while CardHeader's
            hint sits behind ⓘ. */}
        <p className="text-small font-medium text-warning-600">These are shown once and cannot be retrieved.</p>
        <ul className="grid grid-cols-2 gap-1.5 rounded bg-neutral-50 p-3 font-mono text-small text-neutral-800">
          {codes.map((c) => <li key={c} className="tracking-wider">{c}</li>)}
        </ul>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => void copy()}
          >
            Copy all
          </Button>
          <Button variant="secondary" onClick={() => window.print()}>
            Print
          </Button>
        </div>

        <label className="flex min-h-[44px] cursor-pointer items-start gap-2 text-small text-neutral-700">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-6 w-6 shrink-0 rounded border-neutral-300 text-primary-600
                       focus:ring-primary-500 sm:h-4 sm:w-4"
          />
          I have saved these somewhere I can reach without my phone.
        </label>

        <Button onClick={onDone} disabled={!acknowledged} icon={<Check className="h-4 w-4" aria-hidden />}>
          Done
        </Button>
      </CardBody>
    </Card>
  );
}
